/**
 * LLM Pricing Table
 * Per-token costs for all supported providers and models.
 * Prices in USD. Updated manually when provider pricing changes.
 */

export interface ModelPricing {
  provider: string;
  modelPattern: RegExp;
  label: string;
  inputPer1MTokens: number;
  outputPer1MTokens: number;
}

export const LLM_PRICING: ModelPricing[] = [
  // Gemini
  { provider: 'gemini', modelPattern: /gemini-2\.0-flash/,           label: 'Gemini 2.0 Flash',     inputPer1MTokens: 0.10,  outputPer1MTokens: 0.40 },
  { provider: 'gemini', modelPattern: /gemini-2\.5-flash/,           label: 'Gemini 2.5 Flash',     inputPer1MTokens: 0.15,  outputPer1MTokens: 0.60 },
  { provider: 'gemini', modelPattern: /gemini-2\.5-pro/,             label: 'Gemini 2.5 Pro',       inputPer1MTokens: 1.25,  outputPer1MTokens: 10.00 },
  { provider: 'gemini', modelPattern: /gemini-1\.5-flash/,           label: 'Gemini 1.5 Flash',     inputPer1MTokens: 0.075, outputPer1MTokens: 0.30 },
  { provider: 'gemini', modelPattern: /gemini-1\.5-pro/,             label: 'Gemini 1.5 Pro',       inputPer1MTokens: 1.25,  outputPer1MTokens: 5.00 },
  { provider: 'gemini', modelPattern: /gemini/,                      label: 'Gemini (other)',        inputPer1MTokens: 0.15,  outputPer1MTokens: 0.60 },

  // Claude
  { provider: 'claude', modelPattern: /claude-opus-4/,               label: 'Claude Opus 4',        inputPer1MTokens: 15.00, outputPer1MTokens: 75.00 },
  { provider: 'claude', modelPattern: /claude-sonnet-4/,             label: 'Claude Sonnet 4',      inputPer1MTokens: 3.00,  outputPer1MTokens: 15.00 },
  { provider: 'claude', modelPattern: /claude-haiku-4/,              label: 'Claude Haiku 4',       inputPer1MTokens: 0.80,  outputPer1MTokens: 4.00 },
  { provider: 'claude', modelPattern: /claude-3-5-sonnet|claude-sonnet-4-5/, label: 'Claude 3.5/4.5 Sonnet', inputPer1MTokens: 3.00, outputPer1MTokens: 15.00 },
  { provider: 'claude', modelPattern: /claude-3-5-haiku/,            label: 'Claude 3.5 Haiku',     inputPer1MTokens: 0.80,  outputPer1MTokens: 4.00 },
  { provider: 'claude', modelPattern: /claude/,                      label: 'Claude (other)',        inputPer1MTokens: 3.00,  outputPer1MTokens: 15.00 },

  // OpenAI
  { provider: 'openai', modelPattern: /gpt-4o-mini/,                 label: 'GPT-4o Mini',          inputPer1MTokens: 0.15,  outputPer1MTokens: 0.60 },
  { provider: 'openai', modelPattern: /gpt-4o/,                      label: 'GPT-4o',               inputPer1MTokens: 2.50,  outputPer1MTokens: 10.00 },
  { provider: 'openai', modelPattern: /gpt-4-turbo/,                 label: 'GPT-4 Turbo',          inputPer1MTokens: 10.00, outputPer1MTokens: 30.00 },
  { provider: 'openai', modelPattern: /o1-mini/,                     label: 'o1-mini',              inputPer1MTokens: 1.10,  outputPer1MTokens: 4.40 },
  { provider: 'openai', modelPattern: /o1/,                          label: 'o1',                   inputPer1MTokens: 15.00, outputPer1MTokens: 60.00 },
  { provider: 'openai', modelPattern: /openai|gpt/,                  label: 'OpenAI (other)',        inputPer1MTokens: 2.50,  outputPer1MTokens: 10.00 },

  // xAI
  { provider: 'xai',    modelPattern: /grok-4-1-fast/,               label: 'Grok 4.1 Fast',        inputPer1MTokens: 2.00,  outputPer1MTokens: 10.00 },
  { provider: 'xai',    modelPattern: /grok-3-fast/,                 label: 'Grok 3 Fast',          inputPer1MTokens: 5.00,  outputPer1MTokens: 25.00 },
  { provider: 'xai',    modelPattern: /grok-3-mini-fast/,            label: 'Grok 3 Mini Fast',     inputPer1MTokens: 0.30,  outputPer1MTokens: 0.50 },
  { provider: 'xai',    modelPattern: /grok-2/,                      label: 'Grok 2',               inputPer1MTokens: 2.00,  outputPer1MTokens: 10.00 },
  { provider: 'xai',    modelPattern: /grok/,                        label: 'Grok (other)',          inputPer1MTokens: 5.00,  outputPer1MTokens: 25.00 },
];

/**
 * Calculate estimated cost for a given LLM call.
 * First match wins — specific models before catch-all patterns.
 */
export function calculateCost(
  provider: string,
  model: string,
  inputTokens: number,
  outputTokens: number
): number {
  const pricing = LLM_PRICING.find(
    p => p.provider === provider && p.modelPattern.test(model)
  );
  if (!pricing) return 0;

  const inputCost = (inputTokens / 1_000_000) * pricing.inputPer1MTokens;
  const outputCost = (outputTokens / 1_000_000) * pricing.outputPer1MTokens;
  return Math.round((inputCost + outputCost) * 1_000_000) / 1_000_000;
}
